import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Result, Button, Typography, Card } from 'antd';
import { BugOutlined, ReloadOutlined, HomeOutlined } from '@ant-design/icons';

const { Paragraph, Text } = Typography;

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    };
  }

  public static getDerivedStateFromError(error: Error): State {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error,
      errorInfo: null,
      errorId: `error_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Log error to external service (e.g., Sentry)
    this.logErrorToService(error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Call optional error handler
    this.props.onError?.(error, errorInfo);
  }

  private logErrorToService = (error: Error, errorInfo: ErrorInfo) => {
    // Log to console in development
    if ((import.meta as any).env?.DEV) {
      console.group('🐛 Error Boundary - Error Details');
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.error('Component Stack:', errorInfo.componentStack);
      console.error('Error Stack:', error.stack);
      console.groupEnd();
    }

    // In production, send to error tracking service
    // Example: Sentry, LogRocket, etc.
    try {
      // window.Sentry?.captureException(error, {
      //   contexts: {
      //     react: {
      //       componentStack: errorInfo.componentStack,
      //     },
      //   },
      // });
    } catch (loggingError) {
      console.error('Failed to log error to service:', loggingError);
    }
  };

  private handleReload = () => {
    window.location.reload();
  };

  private handleGoHome = () => {
    window.location.href = '/dashboard';
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null,
    });
  };

  private renderErrorDetails = () => {
    const { error, errorInfo, errorId } = this.state;

    if (!(import.meta as any).env?.DEV || !error) return null;

    return (
      <Card 
        title="Error Details (Development Mode)" 
        style={{ marginTop: 24, textAlign: 'left' }}
        size="small"
      >
        <Paragraph>
          <Text strong>Error ID:</Text> <Text code>{errorId}</Text>
        </Paragraph>
        
        <Paragraph>
          <Text strong>Error Message:</Text>
          <br />
          <Text code>{error.message}</Text>
        </Paragraph>

        {error.stack && (
          <Paragraph>
            <Text strong>Stack Trace:</Text>
            <pre style={{ 
              fontSize: '12px', 
              background: 'var(--ant-color-bg-layout)', 
              padding: '8px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '200px',
            }}>
              {error.stack}
            </pre>
          </Paragraph>
        )}

        {errorInfo?.componentStack && (
          <Paragraph>
            <Text strong>Component Stack:</Text>
            <pre style={{ 
              fontSize: '12px', 
              background: 'var(--ant-color-bg-layout)', 
              padding: '8px',
              borderRadius: '4px',
              overflow: 'auto',
              maxHeight: '200px',
            }}>
              {errorInfo.componentStack}
            </pre>
          </Paragraph>
        )}
      </Card>
    );
  };

  public render() {
    const { hasError, errorId } = this.state;
    const { children, fallback } = this.props;

    if (hasError) {
      // Custom fallback UI if provided
      if (fallback) {
        return fallback;
      }

      // Default error UI
      return (
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          flexDirection: 'column',
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '24px',
          background: 'var(--ant-color-bg-layout)',
        }}>
          <div style={{ maxWidth: '600px', width: '100%' }}>
            <Result
              status="error"
              icon={<BugOutlined style={{ color: 'var(--ant-color-error)' }} />}
              title="Something went wrong"
              subTitle={
                <div>
                  <p>
                    We're sorry, but something unexpected happened. Our team has been 
                    notified and will look into this issue.
                  </p>
                  {errorId && (
                    <p>
                      <Text type="secondary">
                        Error ID: <Text code>{errorId}</Text>
                      </Text>
                    </p>
                  )}
                </div>
              }
              extra={[
                <Button 
                  key="retry" 
                  type="primary" 
                  icon={<ReloadOutlined />}
                  onClick={this.handleRetry}
                >
                  Try Again
                </Button>,
                <Button 
                  key="reload" 
                  icon={<ReloadOutlined />}
                  onClick={this.handleReload}
                >
                  Reload Page
                </Button>,
                <Button 
                  key="home" 
                  icon={<HomeOutlined />}
                  onClick={this.handleGoHome}
                >
                  Go Home
                </Button>,
              ]}
            />

            {this.renderErrorDetails()}
          </div>
        </div>
      );
    }

    return children;
  }
}

// Higher-order component for easier usage
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) => {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
};

// Hook for manually reporting errors
export const useErrorHandler = () => {
  const handleError = (error: Error, context?: string) => {
    console.error(`Manual error report${context ? ` (${context})` : ''}:`, error);
    
    // Log to error service
    // window.Sentry?.captureException(error, {
    //   tags: {
    //     context: context || 'manual',
    //   },
    // });
    
    // Optionally show user notification
    // You could integrate with notification system here
  };

  return { handleError };
};
